import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotsNGController as SlotController } from '../../controllers/slots-ng-controller';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';
import '../sic-container/sic-container';
import { backgroundColor } from '../sic-container/sic-container';

import componentStyles from './sic-promo-cutout.css.lit';
import defaultVars from './css-variables/sic-promo-cutout-vars.css.lit';

/**
 * The Promo Cutout component.
 * @element sic-promo-cutout
 * @since 1.0.0
 * @slot heading
 * @slot body
 * @slot cta (cannot go into the shadow dom - a11y)
 * @slot cutout-image
 *
 * @prop {Boolean} media-first - defaults to false (image on right)
 * @prop {backgroundColor} background-color - 'primary-light'|'primary-default'|'secondary-default'|'secondary-light'
 * @prop {String} background-image - url to background image behind cutout image
 *
 * Lightdom styles in sic-promo-cutout.lightdom.css included in /shared.css
 */

@customElement('sic-promo-cutout')
export class SicPromoCutout extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, defaultVars];

  slots = new SlotController(this);
  screens = new ScreenSizeController(this);

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'media-first',
  })
  mediaFirst: false;

  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: backgroundColor = 'primary-default';

  @property({
    type: String,
    reflect: true,
    attribute: 'background-image',
  })
  backgroundImage = '';

  @state() private isStacked = false;

  firstUpdated() {
    this.setVars();
  }
  setVars() {
    if (this.backgroundImage) {
      this.style.setProperty(
        '--sic-promo-cutout--bg-image',
        `url(${this.backgroundImage})`
      );
    }
  }

  render(): TemplateResult {
    this.isStacked = window.innerWidth < (screenSizeMap.get('lg') as number);
    const wrapperClasses = {
      'promo-cutout-layout-wrapper': true,
      'layout-stacked': this.isStacked,
      'layout-media-first': this.mediaFirst,
    };

    // stacked view
    if (this.isStacked) {
      return html`
        <div class="promo-cutout-wrapper">
          <div class="${classMap(wrapperClasses)}">
            <div class="layout-area-media">
              <div class="cutout-bg">
                ${this.backgroundImage !== ''
                  ? html`<div class="cutout-bg-image">
                      <div class="cutout-bg-image-blur"></div>
                    </div>`
                  : ''}
              </div>
              ${this.slots.conditionalSlot('cutout-image', false)}
            </div>
            ${this.contentTemplate()}
          </div>
        </div>
      `;
    } else if (this.mediaFirst) {
      return html`
        <div class="promo-cutout-wrapper">
          <div class="${classMap(wrapperClasses)}">
            <div class="layout-area-media">
              <div class="cutout-bg">
                ${this.backgroundImage !== ''
                  ? html`<div class="cutout-bg-image">
                      <div class="cutout-bg-image-blur"></div>
                    </div>`
                  : ''}
              </div>
            </div>
            ${this.contentTemplate()}
          </div>
          ${this.slots.conditionalSlot('cutout-image', false)}
        </div>
      `;
    } else {
      return html`
        <div class="promo-cutout-wrapper">
          <div class="${classMap(wrapperClasses)}">
            ${this.contentTemplate()}
            <div class="layout-area-media">
              <div class="cutout-bg">
                ${this.backgroundImage !== ''
                  ? html`<div class="cutout-bg-image">
                      <div class="cutout-bg-image-blur"></div>
                    </div>`
                  : ''}
              </div>
            </div>
          </div>
          ${this.slots.conditionalSlot('cutout-image', false)}
        </div>
      `;
    }
  }

  contentTemplate(): TemplateResult {
    return html` <sic-container
      background-color="${this.backgroundColor}"
      container-width="full"
      top-padding="none"
      bottom-padding="none"
      class="layout-area-content"
      ><div class="layout-content-wrapper">
        <div class="content">
          ${this.headingTemplate()} ${this.slots.conditionalSlot('body', false)}
          ${this.slots.conditionalSlot('cta', false)}
        </div>
      </div>
    </sic-container>`;
  }
  headingTemplate(): TemplateResult {
    if (this.slots.slotExists('heading')) {
      return html` <sic-heading level-size="6" class="promo-heading">
        ${this.slots.conditionalSlot('heading')}
      </sic-heading>`;
    }
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-promo-cutout': SicPromoCutout;
  }
}
